import type { FilterFormType } from '@lighthouse/core'
import { filterToString, useApplicationContext, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import { useCallback, useDeferredValue, useMemo } from 'react'
import styled from 'styled-components'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'

const SCxTooltipContainer = styled.div`
    padding: 0 4px;
    color: var(--color-white);
    background: var(--color-gray-900);
`

const SCxTooltipText = styled.p`
    white-space: pre;
`

export const useActionExecuteFilterTooltip = (executeFilter?: FilterFormType) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { personOptions = [], roleOptions = [], departmentOptions = [] } = useApplicationContext()
    const { viewDsId, formDsId } = useActionAdderDepParams()
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const dataSourceList = useDataSourceList(appId, envId)
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))

    const executeFilterLength = useMemo(() => {
        return executeFilter?.expression?.conditions?.length ?? 0
    }, [executeFilter?.expression?.conditions?.length])

    const customViewData = useMemo(() => {
        const viewDataSource = find(item => item.id === viewDsId, dataSourceList)
        return {
            dataSource: viewDataSource,
            name: '自定义视图'
        }
    }, [dataSourceList, viewDsId])

    const executeFilterTitleLines = useMemo(() => {
        if (!executeFilter) {
            return undefined
        }
        return filterToString({
            formDsId,
            customViewData,
            dataSourceList,
            filter: executeFilter,
            personOptions,
            roleOptions,
            departmentOptions,
            availableFieldBlocksInfo,
            pageVariableSchemas,
            pagePathParameter,
            pageQueryParameter,
            availableFilterBlocksInfo
        })
    }, [
        availableFieldBlocksInfo,
        customViewData,
        dataSourceList,
        departmentOptions,
        executeFilter,
        availableFilterBlocksInfo,
        formDsId,
        pagePathParameter,
        pageQueryParameter,
        pageVariableSchemas,
        personOptions,
        roleOptions
    ])

    const tooltipContent = useMemo(() => {
        if (!executeFilterTitleLines || executeFilterTitleLines.length === 0) {
            return undefined
        }
        return (
            <SCxTooltipContainer>
                <SCxTooltipText>满足以下条件时执行该动作：</SCxTooltipText>
                {executeFilterTitleLines.map(str => (
                    <SCxTooltipText key={str}>{str}</SCxTooltipText>
                ))}
            </SCxTooltipContainer>
        )
    }, [executeFilterTitleLines])

    return {
        tooltipContent,
        disabled: !tooltipContent,
        executeFilterLength
    }
}
